import { normalizePage, Page, serviceFetch, serviceJson, TASK_SERVICE_BASE_URL } from './mobileParityApi';

export type DonationPurposeDTO = {
  id: string;
  name: string;
  description?: string | null;
};

export type ContactInfoDTO = {
  name: string;
  phone?: string;
  email?: string;
};

export type CreditReferenceDTO = {
  name: string;
  relationship: string;
  phone?: string;
  email?: string;
};

export type CreateCreditApplicationRequest = {
  description: string;
  purposeIds: string[];
  contactInfo?: ContactInfoDTO | null;
  references?: CreditReferenceDTO[] | null;
  passportPhoto?: string | null;
  photos?: string[];
  documents?: string[];
};

export type CreditDTO = {
  id: string;
  code?: string;
  amount?: number | null;
  balance?: number | null;
  currency?: string | null;
  unit?: 'MONEY' | 'TIME' | string;
  note?: string | null;
  status?: string;
  issuedAt?: string | null;
  expiresAt?: string | null;
  purposes?: DonationPurposeDTO[];
};

export type CreditApplicationDTO = {
  id: string;
  description?: string;
  purposeIds?: string[];
  purposes?: DonationPurposeDTO[];
  passportPhoto?: string | null;
  photos?: string[];
  documents?: string[];
  contactInfo?: ContactInfoDTO | null;
  references?: CreditReferenceDTO[];
  userId?: string;
  status?: 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'DENIED' | 'CANCELLED' | 'CREDIT_ISSUED' | string;
  createdAt?: string;
  updatedAt?: string;
  approvalDenialDate?: string;
  credits?: CreditDTO[];
};

export async function fetchCreditsByUser(
  userId: string,
  page = 0,
  size = 25
): Promise<Page<CreditDTO>> {
  const raw = await serviceJson<CreditDTO[] | Page<CreditDTO>>(
    `${TASK_SERVICE_BASE_URL}/api/v1/credits/user/${encodeURIComponent(userId)}/all?page=${page}&size=${size}`,
    { method: 'GET', acceptJson: true },
    'Failed to load credits'
  );
  return normalizePage(raw);
}

export async function fetchCreditById(creditId: string): Promise<CreditDTO> {
  return serviceJson<CreditDTO>(
    `${TASK_SERVICE_BASE_URL}/api/v1/credits/${encodeURIComponent(creditId)}`,
    { method: 'GET', acceptJson: true },
    'Failed to fetch credit'
  );
}

export async function fetchDonationPurposes({
  active,
  page = 0,
  size = 20,
}: { active?: boolean; page?: number; size?: number } = {}): Promise<Page<DonationPurposeDTO> & { last?: boolean }> {
  const params = new URLSearchParams();
  if (active !== undefined) params.append('active', String(active));
  params.append('page', String(page));
  params.append('size', String(size));

  const raw = await serviceJson<Page<DonationPurposeDTO>>(
    `${TASK_SERVICE_BASE_URL}/api/v1/donation-credit-purposes?${params}`,
    { method: 'GET', acceptJson: true },
    'Failed to load credit purposes'
  );
  return normalizePage(raw);
}

export async function createCreditApplication(
  payload: CreateCreditApplicationRequest
): Promise<CreditApplicationDTO | null> {
  const response = await serviceFetch(
    `${TASK_SERVICE_BASE_URL}/api/v1/credit-applications`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'Failed to submit credit application'
  );
  const text = await response.text().catch(() => '');
  if (!text) return null;

  try {
    return JSON.parse(text) as CreditApplicationDTO;
  } catch {
    return null;
  }
}

export async function fetchApplicationsByUser(
  userId: string,
  opts: { uid?: string; page?: number; size?: number; sort?: string } = {}
): Promise<Page<CreditApplicationDTO>> {
  const params = new URLSearchParams();
  if (opts.uid) params.set('uid', opts.uid);
  if (opts.page != null) params.set('page', String(opts.page));
  if (opts.size != null) params.set('size', String(opts.size));
  if (opts.sort) params.set('sort', opts.sort);

  const raw = await serviceJson<Page<CreditApplicationDTO>>(
    `${TASK_SERVICE_BASE_URL}/api/v1/credit-applications/user/${encodeURIComponent(userId)}${params.toString() ? `?${params}` : ''}`,
    { method: 'GET', acceptJson: true },
    'Failed to load credit applications'
  );

  return hydrateCreditApplicationsPage(normalizePage(raw));
}

export async function fetchApplicationById(id: string): Promise<CreditApplicationDTO> {
  const app = await serviceJson<CreditApplicationDTO>(
    `${TASK_SERVICE_BASE_URL}/api/v1/credit-applications/${encodeURIComponent(id)}`,
    { method: 'GET', acceptJson: true },
    'Failed to load credit application'
  );
  return hydrateCreditApplication(app);
}

export async function deleteApplication(id: string): Promise<void> {
  await serviceFetch(
    `${TASK_SERVICE_BASE_URL}/api/v1/credit-applications/${encodeURIComponent(id)}`,
    { method: 'DELETE', json: false },
    'Failed to delete credit application'
  );
}

export function buildCreateCreditApplicationRequest(args: {
  description: string;
  purposeIds: string[];
  contactInfo?: ContactInfoDTO | null;
  references?: CreditReferenceDTO[] | null;
  passportPhotoId?: string | null;
  photoIds?: string[];
  documentIds?: string[];
}): CreateCreditApplicationRequest {
  return {
    description: args.description.trim(),
    purposeIds: Array.from(new Set(args.purposeIds)),
    contactInfo: args.contactInfo ?? undefined,
    references: args.references?.length ? args.references : undefined,
    passportPhoto: args.passportPhotoId ?? undefined,
    photos: Array.from(new Set(args.photoIds || [])),
    documents: Array.from(new Set(args.documentIds || [])),
  };
}

async function fetchCreditPurposeMap(): Promise<Map<string, DonationPurposeDTO>> {
  try {
    const page = await fetchDonationPurposes({ page: 0, size: 200 });
    return new Map(page.content.map((purpose) => [purpose.id, purpose]));
  } catch {
    return new Map();
  }
}

async function hydrateCreditApplicationsPage(
  pageData: Page<CreditApplicationDTO>
): Promise<Page<CreditApplicationDTO>> {
  const purposeMap = await fetchCreditPurposeMap();
  return {
    ...pageData,
    content: pageData.content.map((app) => attachApplicationPurposes(app, purposeMap)),
  };
}

async function hydrateCreditApplication(app: CreditApplicationDTO): Promise<CreditApplicationDTO> {
  const purposeMap = await fetchCreditPurposeMap();
  return attachApplicationPurposes(app, purposeMap);
}

function attachApplicationPurposes(
  app: CreditApplicationDTO,
  purposeMap: Map<string, DonationPurposeDTO>
): CreditApplicationDTO {
  if (app.purposes?.length) return app;

  const purposes = (app.purposeIds || [])
    .map((id) => purposeMap.get(id))
    .filter((purpose): purpose is DonationPurposeDTO => Boolean(purpose));

  return {
    ...app,
    purposes,
  };
}
